import React from 'react';
import { Circle } from 'lucide-react';

interface LoaderProps {
  size?: number;
  className?: string;
  variant?: 'spinner' | 'dots' | 'pulse' | 'skeleton';
  text?: string;
}

const Loader: React.FC<LoaderProps> = ({
  size = 24,
  className = '',
  variant = 'spinner',
  text
}) => {
  const renderSpinner = () => (
    <div className='flex items-center justify-center'>
      <div className='relative' style={{ width: size, height: size }}>
        <div
          className='border-4 border-blue-200 rounded-full animate-spin'
          style={{ width: size, height: size }}
        >
          <div className='absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-600 rounded-full animate-spin'></div>
        </div>
      </div>
    </div>
  );

  const renderDots = () => (
    <div className='flex items-center justify-center space-x-2'>
      {[0, 1, 2].map(i => (
        <Circle
          key={i}
          size={size / 3}
          className={'animate-pulse text-blue-600'}
          style={{ animationDelay: `${i * 0.2}s` }}
        />
      ))}
    </div>
  );

  const renderPulse = () => (
    <div className='flex items-center justify-center'>
      <div
        className='bg-blue-600 rounded-full animate-ping'
        style={{ width: size, height: size }}
      ></div>
    </div>
  );

  const renderSkeleton = () => (
    <div className='animate-pulse'>
      <div className='h-4 bg-gray-200 rounded w-3/4 mb-2'></div>
      <div className='h-4 bg-gray-200 rounded w-1/2'></div>
    </div>
  );

  const renderContent = () => {
    switch (variant) {
      case 'dots':
        return renderDots();
      case 'pulse':
        return renderPulse();
      case 'skeleton':
        return renderSkeleton();
      default:
        return renderSpinner();
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {renderContent()}
      {text && <p className='mt-3 text-sm text-gray-600 font-medium'>{text}</p>}
    </div>
  );
};

// Page loading component for route transitions
export const PageLoader: React.FC<{ text?: string }> = ({
  text = 'Loading...'
}) => (
  <div className='w-full min-h-screen flex items-center justify-center p-8'>
    <div className='text-center flex flex-col items-center'>
      <div className='relative mb-6 flex justify-center'>
        {/* Outer ring */}
        <div className='relative w-16 h-16 sm:w-16 sm:h-16'>
          <div className='absolute inset-0 border-4 border-blue-200 rounded-full animate-spin'></div>
          <div className='absolute inset-0 border-4 border-transparent border-t-blue-600 rounded-full animate-spin'></div>
          {/* Inner pulse */}
          <div className='absolute inset-0 bg-blue-600 rounded-full animate-ping opacity-20'></div>
        </div>
      </div>
      <p className='text-lg sm:text-xl font-semibold text-slate-700 mb-2'>
        {text}
      </p>
      <p className='text-sm sm:text-base text-slate-500'>
        Please wait while we load your content
      </p>
    </div>
  </div>
);

// Card loading skeleton
export const CardSkeleton: React.FC = () => (
  <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse'>
    <div className='flex items-center space-x-4 mb-4'>
      <div className='w-12 h-12 bg-gray-200 rounded-full'></div>
      <div className='flex-1'>
        <div className='h-4 bg-gray-200 rounded w-3/4 mb-2'></div>
        <div className='h-3 bg-gray-200 rounded w-1/2'></div>
      </div>
    </div>
    <div className='space-y-3'>
      <div className='h-3 bg-gray-200 rounded'></div>
      <div className='h-3 bg-gray-200 rounded w-5/6'></div>
      <div className='h-3 bg-gray-200 rounded w-4/6'></div>
    </div>
  </div>
);

// Table loading skeleton
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 6
}) => (
  <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
    {/* Header */}
    <div className='bg-gray-50 px-6 py-4 border-b border-gray-200'>
      <div className='flex space-x-4'>
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className='h-4 bg-gray-200 rounded flex-1'></div>
        ))}
      </div>
    </div>
    {/* Rows */}
    <div className='divide-y divide-gray-200'>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className='px-6 py-4'>
          <div className='flex space-x-4'>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div
                key={colIndex}
                className='h-4 bg-gray-200 rounded flex-1 animate-pulse'
                style={{ animationDelay: `${(rowIndex + colIndex) * 0.1}s` }}
              ></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Loader;
